import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Trophy, 
  Target, 
  Star, 
  Calendar, 
  Hash,
  TrendingUp,
  Award,
  ChevronRight,
  User
} from "lucide-react";

const API = '/api';

export default function MyResults() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [shooterInfo, setShooterInfo] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMyResults();
    }
  }, [user]);

  const fetchMyResults = async () => {
    try {
      // Get all shooters to find one matching user's linked_shooter_id, email, or shooter_id
      const shootersRes = await axios.get(`${API}/shooters`);
      const shooters = shootersRes.data;
      
      let matchedShooter = null;
      
      // First try by linked_shooter_id (most reliable)
      if (user?.linked_shooter_id) {
        matchedShooter = shooters.find(s => s.id === user.linked_shooter_id);
      }
      
      // Then try by shooter_id (from user profile)
      if (!matchedShooter && user?.shooter_id) {
        matchedShooter = shooters.find(s => s.shooter_id === user.shooter_id);
      }
      
      // Then try by email match
      if (!matchedShooter) {
        matchedShooter = shooters.find(
          s => s.email?.toLowerCase() === user?.email?.toLowerCase()
        );
      }
      
      // Finally try by name match
      if (!matchedShooter) {
        matchedShooter = shooters.find(
          s => s.name?.toLowerCase() === user?.name?.toLowerCase()
        );
      }

      if (matchedShooter) {
        setShooterInfo(matchedShooter);
        
        // Get all entries for this shooter
        const entriesRes = await axios.get(`${API}/entries?shooter_id=${matchedShooter.id}`);
        const entries = entriesRes.data;
        
        // Get all rounds for these entries
        const roundPromises = entries.map(entry => 
          axios.get(`${API}/rounds?entry_id=${entry.id}`)
        );
        const roundResponses = await Promise.all(roundPromises);
        
        // Get event and match info for each entry
        const matchIds = [...new Set(entries.map(e => e.match_id))];
        const eventIds = [...new Set(entries.map(e => e.event_id))];
        
        const [matchesRes, eventsRes] = await Promise.all([
          Promise.all(matchIds.map(id => axios.get(`${API}/matches/${id}`).catch(() => null))),
          Promise.all(eventIds.map(id => axios.get(`${API}/events/${id}`).catch(() => null)))
        ]);
        
        const matchesMap = {};
        matchesRes.forEach(res => {
          if (res?.data) matchesMap[res.data.id] = res.data;
        });
        
        const eventsMap = {};
        eventsRes.forEach(res => {
          if (res?.data) eventsMap[res.data.id] = res.data;
        });
        
        // Compile results by event/match
        const resultsByEvent = {};
        
        entries.forEach((entry, index) => {
          const eventId = entry.event_id;
          const matchId = entry.match_id;
          const event = eventsMap[eventId] || { name: "Unknown Event" };
          const match = matchesMap[matchId] || { name: "Unknown Match" };
          const rounds = roundResponses[index]?.data || [];
          
          if (!resultsByEvent[eventId]) {
            resultsByEvent[eventId] = {
              event,
              matches: {}
            };
          }
          
          if (!resultsByEvent[eventId].matches[matchId]) {
            resultsByEvent[eventId].matches[matchId] = {
              match,
              entry,
              rounds: []
            };
          }
          
          resultsByEvent[eventId].matches[matchId].rounds = rounds;
        });
        
        setResults(resultsByEvent);
      }
    } catch (error) {
      console.error("Failed to fetch results:", error);
      toast.error("Failed to load your results");
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const getStats = () => {
    if (!results) return null;
    
    let totalRounds = 0;
    let totalHits = 0;
    let totalShots = 0;
    let bestScore = 0;
    let bestStreak = 0;
    let totalMatches = 0;
    
    Object.values(results).forEach(eventData => {
      Object.values(eventData.matches).forEach(matchData => {
        totalMatches++;
        matchData.rounds.forEach(round => {
          totalRounds++;
          totalHits += round.total_hits;
          totalShots += round.scores?.length || 0;
          if (round.total_hits > bestScore) bestScore = round.total_hits;
          if (round.longest_streak > bestStreak) bestStreak = round.longest_streak;
        });
      });
    });
    
    return {
      totalRounds,
      totalMatches,
      totalHits,
      totalShots,
      bestScore,
      bestStreak,
      avgScore: totalRounds > 0 ? (totalHits / totalRounds).toFixed(1) : 0,
      hitRate: totalShots > 0 ? ((totalHits / totalShots) * 100).toFixed(1) : 0
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading your results...</div>
      </div>
    );
  }

  if (!shooterInfo) {
    return (
      <div data-testid="my-results-page" className="space-y-6">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            My Results
          </h1>
          <p className="text-slate-500 mt-1">View your scores across all matches</p>
        </div>
        
        <Card>
          <CardContent className="py-12 text-center">
            <User className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="font-semibold text-lg mb-2">No Shooter Profile Found</h3>
            <p className="text-slate-500 mb-4">
              Your account is not linked to a shooter profile yet.
            </p>
            <p className="text-sm text-slate-400">
              Ask an admin to create a shooter profile with your email ({user?.email}) 
              or assign your Shooter ID.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const stats = getStats();

  return (
    <div data-testid="my-results-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            My Results
          </h1>
          <p className="text-slate-500 mt-1">
            {shooterInfo.name}
            {shooterInfo.shooter_id && (
              <Badge variant="outline" className="ml-2 font-mono">
                <Hash size={12} className="mr-1" />
                {shooterInfo.shooter_id}
              </Badge>
            )}
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      {stats && stats.totalRounds > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#1B3B6F]/5 border-[#1B3B6F]/20">
            <CardContent className="p-4 text-center">
              <Target className="mx-auto text-[#1B3B6F] mb-2" size={24} />
              <p className="text-sm text-[#1B3B6F] uppercase tracking-wider">Total Rounds</p>
              <p className="font-mono text-2xl font-bold text-[#1B3B6F]">{stats.totalRounds}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <Trophy className="mx-auto text-green-600 mb-2" size={24} />
              <p className="text-sm text-green-700 uppercase tracking-wider">Best Score</p>
              <p className="font-mono text-2xl font-bold text-green-700">{stats.bestScore}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <TrendingUp className="mx-auto text-amber-600 mb-2" size={24} />
              <p className="text-sm text-amber-700 uppercase tracking-wider">Best Streak</p>
              <p className="font-mono text-2xl font-bold text-amber-700">{stats.bestStreak}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-4 text-center">
              <Award className="mx-auto text-purple-600 mb-2" size={24} />
              <p className="text-sm text-purple-700 uppercase tracking-wider">Hit Rate</p>
              <p className="font-mono text-2xl font-bold text-purple-700">{stats.hitRate}%</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Results by Event */}
      {results && Object.keys(results).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(results).map(([eventId, eventData]) => (
            <Card key={eventId}>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Calendar className="text-[#1B3B6F]" />
                  {eventData.event.name}
                  <Badge 
                    variant={eventData.event.status === "active" ? "default" : "secondary"}
                    className={eventData.event.status === "active" ? "bg-green-100 text-green-800" : ""}
                  >
                    {eventData.event.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {Object.entries(eventData.matches).map(([matchId, matchData]) => (
                  <div 
                    key={matchId} 
                    className="p-4 border-t first:border-t-0 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          {matchData.match.match_number && (
                            <Badge variant="outline" className="font-mono text-xs">
                              #{matchData.match.match_number}
                            </Badge>
                          )}
                          <h4 className="font-semibold">{matchData.match.name}</h4>
                        </div>
                        <p className="text-sm text-slate-500">
                          {matchData.match.shots_per_round} shots per round
                          {matchData.entry.team_name && ` | Team: ${matchData.entry.team_name}`}
                        </p>
                      </div>
                      <Link to={`/leaderboard/${matchId}`}>
                        <Button variant="outline" size="sm">
                          View Leaderboard <ChevronRight size={16} />
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Rounds */}
                    {matchData.rounds.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">
                          Your Rounds ({matchData.rounds.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {matchData.rounds.map((round, idx) => (
                            <div 
                              key={round.id}
                              className={`
                                px-3 py-2 rounded-lg border flex items-center gap-2
                                ${round.is_best_round 
                                  ? "bg-yellow-50 border-yellow-300" 
                                  : "bg-slate-50 border-slate-200"
                                }
                              `}
                            >
                              <span className="text-xs text-slate-400">#{idx + 1}</span>
                              <span className="font-mono font-bold">
                                {round.total_hits}/{matchData.match.shots_per_round}
                              </span>
                              {round.longest_streak > 0 && (
                                <span className="text-xs text-amber-600">
                                  (run: {round.longest_streak})
                                </span>
                              )}
                              {round.is_best_round && (
                                <Star size={14} className="text-yellow-500 fill-yellow-500" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">No rounds recorded yet</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No results found yet.</p>
            <p className="text-sm text-slate-400 mt-2">
              Enter a match and record your scores to see them here.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
